import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { productsStationChainList } from '@/api/generated/products-station-chains/products-station-chains';
import type { StationChainListItem } from '@/api/generated/schemas';
import { StationChainStatus } from '@/api/generated/schemas';
import { Badge, type BadgeTone } from '@/shared/ui';
import { col, filter, ListPage, type ListPageConfig } from '@/shared/ui/list-page';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { stationChainDetailAgenda } from '../detail/stationChainDetailAgenda';
import { STATION_CHAIN_LIST_KEY } from '../internal/stationChainsQueryKeys';

const STATUS_TONE: Record<StationChainStatus, BadgeTone> = {
  [StationChainStatus.Active]: 'success',
  [StationChainStatus.Draft]: 'draft',
  [StationChainStatus.Archived]: 'neutral',
};

/** „2 h 15 min" / „45 min" z celkového počtu minut. */
function formatDuration(min: number | null | undefined): string | null {
  if (min === null || min === undefined) {
    return null;
  }
  const hours = Math.floor(min / 60);
  const minutes = min % 60;
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
}

/**
 * Šablona ListPage tras — deklaruje sloupce a filtry; chování dodá engine `@/shared/ui/list-page`.
 * Klíče filtrů a řazení odpovídají BE `StationChainListSpec`.
 */
function buildStationChainListConfig(
  t: (key: string) => string,
  openChain: (id: string | undefined) => void
): ListPageConfig<StationChainListItem> {
  return {
    key: STATION_CHAIN_LIST_KEY,
    i18n: { namespace: 'products' },
    api: { list: (request, signal) => productsStationChainList(request, signal) },
    rowKey: (item) => item.id,
    defaultSort: { column: 'name', desc: false },
    fulltext: { placeholder: (tt) => tt('stationChainList.search') },
    onOpen: (item) => openChain(item.id),
    create: { label: () => t('stationChainList.new'), onClick: () => openChain(undefined) },
    columns: () => [
      col.mono<StationChainListItem>('code', {
        label: t('stationChainList.cols.code'),
        w: 110,
        frozen: true,
        sortable: true,
      }),
      col.custom<StationChainListItem>({
        key: 'name',
        label: t('stationChainList.cols.name'),
        w: 240,
        frozen: true,
        link: true,
        sortable: true,
        cell: (item) => (
          <span className="gap-phx-2 flex items-center">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="font-semibold">{item.name}</span>
          </span>
        ),
      }),
      col.custom<StationChainListItem>({
        key: 'direction',
        label: t('stationChainList.cols.direction'),
        w: 96,
        sortable: true,
        sortKey: 'direction',
        cell: (item) => (
          <Badge tone={item.isReturn ? 'neutral' : 'info'}>
            {item.isReturn
              ? t('stationChain.direction.return')
              : t('stationChain.direction.outbound')}
          </Badge>
        ),
      }),
      col.num<StationChainListItem>('stopsCount', {
        label: t('stationChainList.cols.stops'),
        w: 80,
        sortable: true,
        sortKey: 'stops',
      }),
      col.custom<StationChainListItem>({
        key: 'distanceKm',
        label: t('stationChainList.cols.distance'),
        w: 96,
        align: 'right',
        sortable: true,
        sortKey: 'distance',
        cell: (item) =>
          item.distanceKm === null || item.distanceKm === undefined ? (
            <span className="text-phx-text-faint">—</span>
          ) : (
            <span className="tabular-nums">{item.distanceKm} km</span>
          ),
      }),
      col.custom<StationChainListItem>({
        key: 'durationMin',
        label: t('stationChainList.cols.duration'),
        w: 110,
        align: 'right',
        sortable: true,
        sortKey: 'duration',
        cell: (item) => {
          const formatted = formatDuration(item.durationMin);
          return formatted ? (
            <span className="tabular-nums">{formatted}</span>
          ) : (
            <span className="text-phx-text-faint">—</span>
          );
        },
      }),
      col.custom<StationChainListItem>({
        key: 'status',
        label: t('stationChainList.cols.status'),
        w: 110,
        sortable: true,
        sortKey: 'status',
        cell: (item) => (
          <Badge tone={STATUS_TONE[item.status] ?? 'neutral'}>
            {t(`stationChain.status.${item.status}`)}
          </Badge>
        ),
      }),
    ],
    filterGroups: () => [{ id: 'basic', label: t('stationChainList.groups.basic') }],
    filters: () => [
      filter.text('name', {
        label: t('stationChainList.filters.name'),
        group: 'basic',
        freq: true,
      }),
      filter.text('code', {
        label: t('stationChainList.filters.code'),
        group: 'basic',
        freq: true,
      }),
    ],
  };
}

/**
 * Listpage tras. ListPage engine neumí akci „založit", proto nad ním vykreslíme hlavičku s tlačítkem
 * „Nová trasa"; otevření řádku i založení míří na `stationChainDetailAgenda` (modální vrstva).
 */
export function StationChainListPage() {
  const { t } = useTranslation('products');
  const navigator = useNavigator();

  const openChain = useCallback(
    (id: string | undefined) => {
      void navigator.openModal(stationChainDetailAgenda, { stationChainId: id });
    },
    [navigator]
  );

  const config = useMemo(() => buildStationChainListConfig(t, openChain), [t, openChain]);

  return <ListPage config={config} />;
}
